import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { concat, of } from 'rxjs';
import {
    finalize,
    first,
    map,
    shareReplay,
    switchMap,
    tap
} from 'rxjs/operators';
import {
    ClientService,
    DynamicFormOptionsService,
    ProviderLinkService
} from 'src/app/core/services';
import { ScrollTopService } from '../../../../core/services/scroll-top.service';
import { GoogleAnalyticsService } from 'src/app/shared/service/google-analytics.service';
import {
    applyFlyoutUpdate,
    buildApplicationPageValueModel,
    buildDynamicForm,
    buildFormValuesFromAnswers,
    initFlyoutObserver,
    matchesConditions,
    normalizeValidatorConfigs,
    positionFlipUpTooltip
} from '../../../../core/helpers';
import { DynamicFormApiService } from '../../../../core/services/dynamic-form-api.service';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import {
    ClientPageSectionModel,
    PageSectionColumnModel,
    PageSectionFieldValidator,
    PageSectionRowModel
} from '../../../../domain/models/dynamic-form-schema.model';
import { FormPage } from '../../../../domain/models/form-page.enum';
import { ChangesNotSavedComponent } from '../../../../shared/components/changes-not-saved';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { SectionFlyoutComponent } from '../../components/section-flyout/section-flyout.component';
import { Application, SelectedProvider } from '../../models';
import { applicationPresent } from '../../operators';
import { ApplicationService, CurrentApplicationService } from '../../services';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./../../application-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        DynamicFormComponent,
        FlexLayoutModule,
        MatDividerModule,
        SectionFlyoutComponent
    ]
})
export class PersonalInformationComponent
    extends ChangesNotSavedComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    /**
     * Programs, Providers and Sites selected on Provider page are stored in sessionStorage
     */
    private providerIdsFromProviderPage: Number[] | undefined;
    private programIdsFromProviderPage: Number[] | undefined;
    private providerSiteIdsFromProviderPage: Number[] | undefined;

    /**
     * List to populate the Providers flyout
     */
    selectedProviders: SelectedProvider[] | undefined;
    submitted = false; // because we are extending ChangesNotSavedComponent
    submitting = false;
    activeFlyoutIndex = -1;
    flyoutFlipUp = false;
    private applicationId?: number;
    private loadedSections: ClientPageSectionModel[] = [];
    private flyoutObserver?: IntersectionObserver;

    @ViewChildren('sectionWrapper') sectionWrappers!: QueryList<ElementRef>;

    personalInformation: UntypedFormGroup;

    client$ = this._client.latest;

    formSections$ = this.dynamicFormApi
        .getFormSections(FormPage.personalInformation)
        .pipe(
            map((sections) => {
                this.dynamicFormOptionsService.enrichSectionsWithOptions(
                    sections
                );

                return sections;
            }),
            shareReplay(1)
        );

    formValue$ = this._currentApplication.latest.pipe(
        applicationPresent(),
        first(),
        switchMap((application: Application) => {
            if (application?.applicationId) {
                this.applicationId = application.applicationId;
                return this.applicationService.getDynamicFormValues(
                    application.applicationId!,
                    FormPage.personalInformation
                );
            } else return of(null);
        })
    );

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly applicationService: ApplicationService,
        private readonly snackBar: MatSnackBar,
        private readonly router: Router,
        private readonly _transloco: TranslocoService,
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _client: ClientService,
        private readonly _googleAnalyticsService: GoogleAnalyticsService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly dynamicFormApi: DynamicFormApiService,
        private readonly dynamicFormOptionsService: DynamicFormOptionsService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _scrollTop: ScrollTopService,
        private readonly _providerLink: ProviderLinkService
    ) {
        super();
        this.personalInformation = this._fb.group({});
    }

    ngAfterViewInit() {
        this.flyoutObserver = initFlyoutObserver(
            this.sectionWrappers,
            () => this._updateActiveFlyout(),
            this.destroyRef
        );
    }

    ngOnDestroy() {
        this.flyoutObserver?.disconnect();
    }

    private _updateActiveFlyout(): void {
        applyFlyoutUpdate(
            this.sectionWrappers,
            this._cdr,
            () => this._positionFlipUpTooltip(),
            (state) => {
                this.activeFlyoutIndex = state.activeFlyoutIndex;
                this.flyoutFlipUp = state.flyoutFlipUp;
            }
        );
    }

    private _positionFlipUpTooltip(): void {
        positionFlipUpTooltip(
            this.activeFlyoutIndex,
            this.flyoutFlipUp,
            this.sectionWrappers
        );
    }

    ngOnInit() {
        this._googleAnalyticsService.pageView(
            'Application: Personal Information'
        );
        this.programIdsFromProviderPage =
            this.sessionStorageService.getValues<number>(
                'applicationProgramIds'
            );
        this.providerIdsFromProviderPage =
            this.sessionStorageService.getValues<number>(
                'applicationProviderIds'
            );
        this.providerSiteIdsFromProviderPage =
            this.sessionStorageService.getValues<number>(
                'applicationProviderSiteIds'
            );

        this.formSections$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((sections) => {
                this.loadedSections = sections;
                this.personalInformation = buildDynamicForm(sections, this._fb);

                this.formValue$.pipe(first()).subscribe((formValue) => {
                    if (!formValue) {
                        return;
                    }

                    const values = buildFormValuesFromAnswers(
                        sections,
                        formValue
                    );
                    this.personalInformation.patchValue(values);
                    setTimeout(() => this._scrollTop.scrollToTop(), 0);
                });
            });

        this._currentApplication.latest
            .pipe(
                applicationPresent(),
                first(),
                switchMap((application: Application) => {
                    if (application?.applicationId) {
                        this.applicationId = application.applicationId;
                        const getProviderPrograms =
                            this.applicationService.getSelectedProviderPrograms(
                                application.applicationId!
                            );

                        const updateProviders = !application.isSubmitted
                            ? this.applicationService.patchCurrentApplication({
                                  applicationId: application.applicationId!,
                                  selectedProgramIds:
                                      this.programIdsFromProviderPage,
                                  selectedProviderIds:
                                      this.providerIdsFromProviderPage,
                                  selectedProviderSiteIds:
                                      this.providerSiteIdsFromProviderPage,
                                  portalApplicationSource:
                                      this.sessionStorageService.getValue<string>(
                                          'applicationSource'
                                      ) ?? undefined
                              })
                            : '';

                        // if providers were not selected on the Provider page, let's retrieve the previously saved providers,
                        // otherwise, persist the selected providers and return those
                        const selectedProviders =
                            this.providerIdsFromProviderPage === undefined ||
                            this.providerIdsFromProviderPage.length === 0
                                ? getProviderPrograms
                                : concat(updateProviders, getProviderPrograms);

                        return selectedProviders;
                    } else return of(null);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (res) => {
                    if (res === null || res === undefined) {
                        this.providerIdsFromProviderPage = undefined;
                        this.programIdsFromProviderPage = undefined;
                        this.sessionStorageService.setValue(
                            'applicationProgramIds',
                            ''
                        );
                        this.sessionStorageService.setValue(
                            'applicationProviderIds',
                            ''
                        );
                        this.sessionStorageService.setValue(
                            'applicationProviderSiteIds',
                            ''
                        );

                        this._currentApplication.load();
                    } else this.selectedProviders = res;
                },
                error: (err) => {
                    this.snackBar.open(
                        this._transloco.translate('PROVIDER_SAVE_ERROR') ||
                            'Unable to save your provider selection. Please select a provider again.',
                        this._transloco.translate('CLOSE'),
                        { duration: 10000 }
                    );

                    const providerId =
                        this._providerLink.getProviderIdWithFallback();
                    if (providerId) {
                        this.router.navigate([
                            `${runtimeConfig.clientCode}/providers/${providerId}`
                        ]);
                    } else {
                        this.router.navigate([
                            `${runtimeConfig.clientCode}/provider`
                        ]);
                    }
                }
            });
    }

    getSectionValidators(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        return normalizeValidatorConfigs(section.validators, {
            forceUniqueTargetKeys: true
        });
    }

    isSectionVisible(section: ClientPageSectionModel): boolean {
        return matchesConditions(this.personalInformation, section.visibleWhen);
    }

    getSectionErrors(
        section: ClientPageSectionModel
    ): PageSectionFieldValidator[] {
        const anyFieldTouched = section.rows
            .reduce(
                (acc: PageSectionColumnModel[], row: PageSectionRowModel) =>
                    acc.concat(row.fields),
                []
            )
            .some(
                (field: PageSectionColumnModel) =>
                    this.personalInformation.get(field.key)?.touched
            );

        if (!this.submitted && !anyFieldTouched) {
            return [];
        }

        return this.getSectionValidators(section).filter(
            (validator) =>
                !validator.target &&
                this.personalInformation.hasError(
                    validator.errorKey ?? validator.name
                )
        );
    }

    isChanged(): boolean {
        return this.personalInformation?.touched ?? false;
    }

    isSaved(): boolean {
        return this.submitted;
    }

    onSubmitted() {
        if (this.submitting) return;
        this.personalInformation.markAllAsTouched();
        this.submitted = true;

        if (this.personalInformation.invalid) {
            this._transloco
                .selectTranslate('application.snack.invalid-form')
                .pipe(first())
                .subscribe((message) =>
                    this.snackBar.open(message, undefined, { duration: 4000 })
                );
            return;
        }

        if (!this.applicationId || this.loadedSections.length === 0) {
            return;
        }

        this.submitting = true;

        const payload = buildApplicationPageValueModel(
            this.loadedSections,
            this.personalInformation,
            this.applicationId,
            FormPage.personalInformation
        );

        this.applicationService
            .saveDynamicFormValues(
                this.applicationId,
                FormPage.personalInformation,
                payload
            )
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this.snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.postSubmit();
                }),
                finalize(() => (this.submitting = false))
            )
            .subscribe();
    }

    private postSubmit() {
        this.submitted = true;
        this._currentApplication.load();
        this.router.navigateByUrl(
            `/${runtimeConfig.clientCode}/application/background`
        );
    }
}
